import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import type { AdminInviteInfo } from '@proptr/shared';
import { ApiError } from '@proptr/api-client';
import { AlertIcon, ArrowIcon, AuthShell } from '../components/layout/AuthShell.js';
import { client } from '../main.js';

const MIN_PASSWORD = 8;

/**
 * Landing page for an admin invite email. The invitee confirms their name
 * and sets a password; that activates the account, and they're sent to
 * the normal sign-in (password + emailed code) with their email filled in.
 */
export function AcceptInvitePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [invite, setInvite] = useState<AdminInviteInfo | null>(null);
  const [invalid, setInvalid] = useState(false);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) {
      setInvalid(true);
      return;
    }
    let alive = true;
    client.auth
      .adminInvite(token)
      .then((info) => {
        if (!alive) return;
        setInvite(info);
        setName(info.name ?? '');
      })
      .catch(() => alive && setInvalid(true));
    return () => {
      alive = false;
    };
  }, [token]);

  const mismatch = confirm.length > 0 && confirm !== password;
  const valid = password.length >= MIN_PASSWORD && password === confirm;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!valid || saving) return;
    setSaving(true);
    setError(null);
    try {
      const res = await client.auth.acceptAdminInvite({
        token,
        password,
        name: name.trim() || undefined,
      });
      navigate('/login', {
        replace: true,
        state: { email: res.email, notice: 'Your password is set. Sign in to continue.' },
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong — please try again.');
      setSaving(false);
    }
  }

  return (
    <AuthShell
      eyebrow="You’re invited"
      headline={
        <>
          Join the console.
          <br />
          <em>Set your password.</em>
        </>
      }
    >
      {invalid ? (
        <div className="auth-form">
          <h1>Invite not valid</h1>
          <p className="auth-subtitle">
            This invite link is invalid, already used, or has expired. Ask an admin to
            resend it.
          </p>
          <button type="button" className="auth-primary-button" onClick={() => navigate('/login')}>
            Go to sign in <ArrowIcon />
          </button>
        </div>
      ) : !invite ? (
        <div className="auth-form">
          <p className="auth-subtitle">Checking your invite…</p>
        </div>
      ) : (
        <form className="auth-form" noValidate onSubmit={(e) => void submit(e)}>
          <h1>Set your password</h1>
          <p className="auth-subtitle">
            {invite.invitedBy ? `${invite.invitedBy} invited ` : 'You’re invited as '}
            <strong>{invite.email}</strong> to the admin console.
          </p>

          {error && (
            <div className="auth-banner" role="alert">
              <AlertIcon />
              <p>{error}</p>
            </div>
          )}

          <label htmlFor="name">Your name</label>
          <input
            id="name"
            type="text"
            autoComplete="name"
            placeholder="First and last name"
            value={name}
            disabled={saving}
            onChange={(e) => setName(e.target.value)}
            style={{ marginBottom: 16 }}
          />

          <label htmlFor="password">Password</label>
          <div style={{ position: 'relative', marginBottom: 16 }}>
            <input
              id="password"
              type={show ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder={`At least ${MIN_PASSWORD} characters`}
              className="auth-password-input"
              value={password}
              disabled={saving}
              autoFocus
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setShow((v) => !v)}
              aria-label={show ? 'Hide password' : 'Show password'}
              tabIndex={-1}
              style={{
                position: 'absolute',
                right: 14,
                top: '50%',
                transform: 'translateY(-50%)',
                border: 0,
                background: 'none',
                cursor: 'pointer',
                color: '#9a9ab4',
                display: 'flex',
              }}
            >
              {show ? <EyeOff size={17} /> : <Eye size={17} />}
            </button>
          </div>

          <label htmlFor="confirm">Confirm password</label>
          <input
            id="confirm"
            type={show ? 'text' : 'password'}
            autoComplete="new-password"
            className="auth-password-input"
            value={confirm}
            disabled={saving}
            aria-invalid={mismatch}
            onChange={(e) => setConfirm(e.target.value)}
          />
          {mismatch && (
            <p style={{ margin: '8px 0 0', fontSize: 13, color: '#b42318' }}>
              The passwords don’t match.
            </p>
          )}

          <button
            type="submit"
            className="auth-primary-button auth-login-button"
            disabled={!valid || saving}
            aria-busy={saving}
          >
            {saving ? (
              <>
                <span className="auth-spinner" aria-hidden="true" /> Saving
              </>
            ) : (
              <>
                Set password <ArrowIcon />
              </>
            )}
          </button>
        </form>
      )}
    </AuthShell>
  );
}
